/**
 * UploadTelemetryPanel — chart + event log for a phone's upload pipeline.
 *
 * Two data sources from `/api/fleet/devices/<id>/upload-telemetry`:
 *
 *   - samples: time-series of (backlog, bytes_uploaded, online) sampled
 *     ~every 30 s on the status frame. We derive two bitrates client-side:
 *
 *       actual_upload_mbps = bytes_uploaded * 8 / Δt / 1e6
 *       produced_mbps      = actual_upload_mbps + Δbacklog_total * 8 / Δt
 *
 *     `produced_mbps` represents the recording-produced bitrate — what
 *     the encoder is emitting per second. `actual_upload_mbps` is how
 *     much got through to S3 over the same window. When the link is
 *     healthy they track; during a stall produced flatlines at the
 *     encoder rate while upload falls toward zero and backlog grows.
 *
 *   - events: discrete drop/resume rows. Rendered as ReferenceLines on
 *     the chart + as a chronological log under it. Each row shows
 *     backlog at the moment so the operator can read "lost connection
 *     at 8 MB, came back at 24 MB → 16 MB queued during outage".
 *
 * Refresh: poll every 8 s; the `since_ms` parameter trims the response
 * to new rows only.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  Brush,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownUp } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import api from '../services/api';
import { UploadEvent, UploadSample } from '../types';

interface UploadTelemetryPanelProps {
  /** Device-scoped (DeviceDetail): the device's last ~6 h ring. */
  deviceId?: string;
  /** Session-scoped (/play/<sid>): the full saved pipeline history for one
   *  session, work-start → drain-complete. Set exactly one of these. */
  sessionId?: string;
}

interface ChartPoint {
  t: number;
  produced_mbps: number | null;
  upload_mbps: number | null;
  backlog_mb: number | null;
  online: boolean;
}

// Derive chart points from raw samples. Each point's bitrates are the
// rate from the previous sample to this one. First point has nulls
// because we have no Δt yet.
function deriveChartPoints(samples: UploadSample[]): ChartPoint[] {
  const out: ChartPoint[] = [];
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i];
    const total_mb = (s.backlog_stream_mb ?? 0) + (s.backlog_archive_mb ?? 0);
    if (i === 0) {
      out.push({
        t: s.ts_ms,
        produced_mbps: null,
        upload_mbps: null,
        backlog_mb: total_mb,
        online: s.online,
      });
      continue;
    }
    const prev = samples[i - 1];
    const dt_s = (s.ts_ms - prev.ts_ms) / 1000;
    if (dt_s <= 0) {
      out.push({
        t: s.ts_ms,
        produced_mbps: null,
        upload_mbps: null,
        backlog_mb: total_mb,
        online: s.online,
      });
      continue;
    }
    const upload_mbps = (s.bytes_uploaded * 8) / dt_s / 1_000_000;
    const prev_total = (prev.backlog_stream_mb ?? 0) + (prev.backlog_archive_mb ?? 0);
    const d_backlog_mb = total_mb - prev_total;
    // produced = uploaded + Δbacklog/Δt
    // d_backlog is MB; convert to Mb (×8) then /dt to get Mbps.
    const produced_mbps = upload_mbps + (d_backlog_mb * 8) / dt_s;
    out.push({
      t: s.ts_ms,
      produced_mbps: Math.max(0, produced_mbps),
      upload_mbps: Math.max(0, upload_mbps),
      backlog_mb: total_mb,
      online: s.online,
    });
  }
  return out;
}

function formatTimeHhMmSs(ms: number): string {
  return new Date(ms).toISOString().substr(11, 8);
}

// Render a duration of recorded-video-pending as something the operator
// can read at a glance. The backend's `resume` events at one fixed wall
// time can spam thousands of entries; we'd rather present the operator
// with "~4 min 27 s of video queued" than a raw MB count.
function formatVideoSeconds(secs: number): string {
  if (!isFinite(secs) || secs < 0) return '—';
  if (secs < 1) return '< 1 s';
  if (secs < 60) return `${secs.toFixed(0)} s`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return s === 0 ? `${m} min` : `${m} min ${s} s`;
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// Defensive dedupe on `(kind, ts_ms)`. The backend's resume events have
// been observed firing many times at the exact same wall timestamp on
// reconnect (one entry per retry attempt) — stacking them on the chart
// as ReferenceLines paints the same vertical bar repeatedly and bloats
// react state. Keep one entry per unique event.
function dedupeEvents(events: UploadEvent[]): UploadEvent[] {
  const seen = new Set<string>();
  const out: UploadEvent[] = [];
  for (const e of events) {
    const k = `${e.kind}-${e.ts_ms}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
}

// Device view is a rolling ~6h window (matches the "last ~6 h" label).
// Floors the initial fetch + clamps the rendered time axis so stale idle
// samples from the server's 24h ring can't stretch the chart to a prior day.
const DEVICE_WINDOW_MS = 6 * 60 * 60 * 1000;

export const UploadTelemetryPanel: React.FC<UploadTelemetryPanelProps> = ({ deviceId, sessionId }) => {
  const [samples, setSamples] = useState<UploadSample[]>([]);
  const [events, setEvents] = useState<UploadEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const lastTsRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    // Full pull on mount (no since_ms); incremental polls after.
    lastTsRef.current = 0;
    setSamples([]);
    setEvents([]);
    setLoaded(false);

    const tick = async () => {
      try {
        const data = sessionId
          ? await api.getSessionUploadTelemetry(sessionId, {
              since_ms: lastTsRef.current || undefined,
              limit: 5000,
            })
          : await api.getDeviceUploadTelemetry(deviceId as string, {
              // Floor the initial pull to the rolling window so stale idle
              // samples (the server's 24h ring) from a prior day don't load
              // and stretch the chart. Polls keep using lastTsRef (newer only).
              since_ms: lastTsRef.current || Date.now() - DEVICE_WINDOW_MS,
              limit: 720,
            });
        if (cancelled) return;
        if (lastTsRef.current === 0) {
          setSamples(data.samples);
          setEvents(dedupeEvents(data.events));
        } else {
          if (data.samples.length > 0) {
            setSamples((prev) => {
              const merged = [...prev, ...data.samples];
              // Keep last 720 to bound memory if user leaves panel open.
              return merged.length > 720 ? merged.slice(-720) : merged;
            });
          }
          if (data.events.length > 0) {
            setEvents((prev) => {
              const merged = dedupeEvents([...prev, ...data.events]);
              return merged.length > 200 ? merged.slice(-200) : merged;
            });
          }
        }
        const allTs: number[] = [];
        if (data.samples.length > 0) allTs.push(data.samples[data.samples.length - 1].ts_ms);
        if (data.events.length > 0) allTs.push(data.events[data.events.length - 1].ts_ms);
        if (allTs.length > 0) {
          lastTsRef.current = Math.max(lastTsRef.current, ...allTs);
        }
        setLoaded(true);
      } catch (err) {
        if (!cancelled) setLoaded(true); // swallow + show empty state
      }
    };

    void tick();
    const id = window.setInterval(tick, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [deviceId, sessionId]);

  const chartPoints = useMemo(() => deriveChartPoints(samples), [samples]);

  // Device view is a rolling ~6h window; clamp the RENDERED points to it so a
  // stale idle sample lingering in state can't stretch the time axis across a
  // prior day. Session view shows the whole session. (Summary tiles still use
  // chartPoints — they read the freshest point, which the window never drops.)
  const windowedPoints = useMemo(() => {
    if (sessionId) return chartPoints;
    const cutoff = Date.now() - DEVICE_WINDOW_MS;
    return chartPoints.filter((p) => p.t >= cutoff);
  }, [chartPoints, sessionId]);

  const hasData = windowedPoints.length > 1;

  // Operator-facing summary numbers — pulled from the freshest available
  // point in either dataset. Renders as a strip above the chart so the
  // operator gets "how many seconds of video are queued, how fast is it
  // moving" at a glance without parsing the curves.
  const summary = useMemo(() => {
    const latestSample = samples.length > 0 ? samples[samples.length - 1] : null;
    const latestPoint = chartPoints.length > 0 ? chartPoints[chartPoints.length - 1] : null;
    const latestEvent = events.length > 0 ? events[events.length - 1] : null;
    // Split tiles per field-test feedback: operator needs to see if
    // preview is falling behind separately from archive, because they
    // have very different upload policies (preview = cellular-allowed,
    // archive = WiFi-only on metered links).
    const previewMb = latestSample?.backlog_stream_mb ?? null;
    const archiveMb = latestSample?.backlog_archive_mb ?? null;
    const backlogMb =
      previewMb != null || archiveMb != null
        ? (previewMb ?? 0) + (archiveMb ?? 0)
        : null;
    const uploadMbps = latestPoint?.upload_mbps ?? null;
    const producedMbps = latestPoint?.produced_mbps ?? null;
    // Convert MB → seconds of video using the recording bitrate. MB×8
    // gives megabits; divide by Mb/s yields seconds. The encoder rate
    // is the right denominator (not the upload rate) because backlog
    // is sized in encoded bytes, not in playback seconds.
    let pendingSecs: number | null = null;
    if (backlogMb != null && producedMbps != null && producedMbps > 0.01) {
      pendingSecs = (backlogMb * 8) / producedMbps;
    }
    // Link status: derive from the freshest of (latest sample, latest
    // drop/resume event) — whichever has the higher `ts_ms`. Samples
    // alone aren't reliable here because a disconnected phone stops
    // emitting status heartbeats, so the most recent sample can stay
    // `online=true` forever while the backend has already logged a
    // `drop` event (it fires from the WS `finally` path on disconnect).
    // The connectivity log was the operator's escape hatch for that
    // mismatch; with the log removed, the Link tile has to surface it.
    let online: boolean | null = null;
    let onlineAtMs: number | null = null;
    if (latestSample) {
      online = latestSample.online;
      onlineAtMs = latestSample.ts_ms;
    }
    if (latestEvent && (onlineAtMs == null || latestEvent.ts_ms > onlineAtMs)) {
      online = latestEvent.kind === 'resume';
      onlineAtMs = latestEvent.ts_ms;
    }
    return {
      previewMb,
      archiveMb,
      backlogMb,
      uploadMbps,
      producedMbps,
      pendingSecs,
      online,
      onlineAtMs,
    };
  }, [samples, chartPoints, events]);

  return (
    <Card className="border-border bg-bg-surface md:col-span-2">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <ArrowDownUp className="size-4 text-accent-purple" />
          <h2 className="text-base font-medium text-accent">Upload pipeline</h2>
          <span className="text-xs text-fg-muted">
            recording-produced bitrate vs. actual upload speed
            {sessionId ? ' · this session' : ' · last ~6 h'}
          </span>
        </div>

        {/* Summary strip: four tiles the operator wants at a glance.
            Low- and high-quality backlog are split per field-test
            feedback — on a metered link high quality is gated
            (allow_cellular=0) while low quality keeps flowing, so a growing
            low-quality backlog vs a growing high-quality backlog has very
            different meanings. Visible
            in both populated and empty states (placeholders when data
            hasn't arrived yet) so the layout doesn't lurch when samples
            start flowing in. */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded border border-border bg-bg-base/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-fg-muted">
              Low quality backlog
            </div>
            <div className="mt-1 font-mono text-base text-fg">
              {summary.previewMb != null ? `${summary.previewMb.toFixed(1)} MB` : '—'}
            </div>
            <div className="text-[11px] text-fg-muted">
              low quality (cellular OK)
            </div>
          </div>
          <div className="rounded border border-border bg-bg-base/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-fg-muted">
              High quality backlog
            </div>
            <div className="mt-1 font-mono text-base text-fg">
              {summary.archiveMb != null ? `${summary.archiveMb.toFixed(1)} MB` : '—'}
            </div>
            <div className="text-[11px] text-fg-muted">
              {summary.pendingSecs != null
                ? `total ~${formatVideoSeconds(summary.pendingSecs)} video`
                : 'high quality (WiFi-only)'}
            </div>
          </div>
          <div className="rounded border border-border bg-bg-base/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-fg-muted">
              Upload speed
            </div>
            <div className="mt-1 font-mono text-base text-fg">
              {summary.uploadMbps != null ? `${summary.uploadMbps.toFixed(2)} Mb/s` : '—'}
            </div>
            <div className="text-[11px] text-fg-muted">
              {summary.producedMbps != null
                ? `producing ${summary.producedMbps.toFixed(2)} Mb/s`
                : 'actual link throughput'}
            </div>
          </div>
          <div className="rounded border border-border bg-bg-base/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-fg-muted">
              Link
            </div>
            <div
              className={
                'mt-1 font-mono text-base ' +
                (summary.online === true
                  ? 'text-status-online'
                  : summary.online === false
                    ? 'text-status-error'
                    : 'text-fg')
              }
            >
              {summary.online === true
                ? 'Online'
                : summary.online === false
                  ? 'Offline'
                  : '—'}
            </div>
            <div className="text-[11px] text-fg-muted">
              {summary.onlineAtMs != null
                ? `as of ${formatTimeHhMmSs(summary.onlineAtMs)}`
                : 'awaiting first heartbeat'}
            </div>
          </div>
        </div>

        {!loaded && (
          <div className="text-xs text-fg-muted">loading…</div>
        )}

        {loaded && !hasData && (
          <div className="text-xs text-fg-muted">
            No upload-telemetry samples yet — chart fills in after the phone has sent
            a few status heartbeats while recording.
          </div>
        )}

        {hasData && (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={windowedPoints}
                margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
              >
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis
                  dataKey="t"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => formatTimeHhMmSs(Number(t))}
                  stroke="#666"
                  tick={{ fontSize: 10 }}
                  tickCount={5}
                />
                <YAxis
                  yAxisId="bitrate"
                  stroke="#666"
                  tick={{ fontSize: 10 }}
                  label={{
                    value: 'Mb/s',
                    position: 'insideTopLeft',
                    fill: '#666',
                    fontSize: 10,
                  }}
                />
                <YAxis
                  yAxisId="backlog"
                  orientation="right"
                  stroke="#666"
                  tick={{ fontSize: 10 }}
                  label={{
                    value: 'backlog MB',
                    angle: 90,
                    position: 'insideTopRight',
                    fill: '#666',
                    fontSize: 10,
                  }}
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#14141f',
                    border: '1px solid #2a2a35',
                    color: '#ccc',
                    fontSize: 12,
                  }}
                  labelFormatter={(t) =>
                    new Date(Number(t)).toISOString().replace('T', ' ').replace('Z', '')
                  }
                  formatter={(value: any, name: any) => {
                    if (value == null) return ['—', name];
                    if (name === 'backlog') {
                      return [`${Number(value).toFixed(1)} MB`, name];
                    }
                    return [`${Number(value).toFixed(2)} Mb/s`, name];
                  }}
                />
                {/* Backlog area (right axis) sits behind the bitrate lines
                    so growing queues are visible as a shaded ramp. */}
                <Area
                  yAxisId="backlog"
                  type="monotone"
                  dataKey="backlog_mb"
                  stroke="#3a3a55"
                  fill="#3a3a55"
                  fillOpacity={0.25}
                  name="backlog"
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="bitrate"
                  type="monotone"
                  dataKey="produced_mbps"
                  stroke="#a78bfa"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  name="produced"
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="bitrate"
                  type="monotone"
                  dataKey="upload_mbps"
                  stroke="#00ff00"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  name="uploaded"
                  isAnimationActive={false}
                />
                {events.map((e) => (
                  <ReferenceLine
                    key={`${e.kind}-${e.ts_ms}`}
                    yAxisId="bitrate"
                    x={e.ts_ms}
                    stroke={e.kind === 'drop' ? '#ef4444' : '#22c55e'}
                    strokeDasharray="2 2"
                    strokeOpacity={0.7}
                  />
                ))}
                {/* Bottom slider: drag the handles to focus the chart on a
                    sub-range; data outside the window drops out of the plot.
                    View-only + resets on reload (temporary "trim"). Index-
                    based — with the live 8s poll a held selection can shift
                    slightly as new points append (acceptable for a focus
                    tool; controlled+poll-pause is the polish step). */}
                <Brush
                  dataKey="t"
                  height={22}
                  travellerWidth={8}
                  stroke="#a855f7"
                  fill="#14141f"
                  tickFormatter={(t) => formatTimeHhMmSs(Number(t))}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {hasData && (
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-fg-muted">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3" style={{ backgroundColor: '#a78bfa' }} />
              produced (recording bitrate)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3" style={{ backgroundColor: '#00ff00' }} />
              uploaded (actual)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3 opacity-50" style={{ backgroundColor: '#3a3a55' }} />
              backlog (right axis)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3" style={{ backgroundColor: '#ef4444' }} />
              drop
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-3" style={{ backgroundColor: '#22c55e' }} />
              resume
            </span>
          </div>
        )}

      </CardContent>
    </Card>
  );
};

export default UploadTelemetryPanel;
